import React, { useState, useEffect } from 'react';
import { usePrevious } from 'Hooks/CustomHooks';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Line } from 'Common/Controls/Dms/MultiMsg/Line';
import { LineAlignment } from 'DataLibrary/Dms/MultiMsgConstants';

const BackspaceKeyCode = 8;
const EnterKeyCode = 13;

const useStyles = makeStyles((theme: Theme) => ({
    input: {
        border: 'none',
        backgroundColor: '#333',
        opacity: 1,
        '-webkit-text-fill-color': 'yellow',
        color: 'yellow',
        width: 'calc(100% - 8)',
        margin: 4
    }
}));

type alignmentStyles = 'center' | 'left' | 'right'

export enum FocusPosition {
    Start,
    End
}

interface IDynamicStyles {
    backgroundColor: string;
    textAlign: alignmentStyles;
}

const getStyles = (alignment: LineAlignment, isEditable?: boolean): IDynamicStyles => {
    const style = {
        backgroundColor: isEditable ? '#333' : '#222',
        textAlign: 'center' as alignmentStyles
    };

    switch (alignment) {
        case LineAlignment.Left:
            style.textAlign = 'left' as alignmentStyles;
            break;
        case LineAlignment.Right:
            style.textAlign = 'right' as alignmentStyles;
            break;
    }

    return style;
};

interface IProps {
    text: string;
    line: Line;
    onFocus: () => void;
    onBlur: () => void;
    onShiftFocus: (targetIndex: number) => void;
    focused: FocusPosition | null;
    editable?: boolean;
}

const MultiMsgLineDisplay: React.FC<IProps> = ({ text, line, onFocus, onBlur, onShiftFocus, focused, editable }: IProps) => {
    const classes = useStyles();
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>();
    const [cursorIndex, setCursorIndex] = useState<number>(-1);
    const [value, setValue] = useState<string>('');
    const [nextValue, setNextValue] = useState<string>('');

    const prevLine = usePrevious(line);

    const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const uppercaseValue = evt.target.value.toLocaleUpperCase();
        setValue(uppercaseValue);
        const overflow = line.update(uppercaseValue);
        if (overflow.length > 0) {
            onShiftFocus(line.index + 1);
        }
    };

    const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        evt.stopPropagation();

        const cursorIndex = evt.currentTarget.selectionStart;
        const isStartOfLine = cursorIndex === 0;
        const keyCode = evt.keyCode;

        switch (keyCode) {
            case EnterKeyCode:
                if (cursorIndex != null) {
                    const newValue = `${value.substring(0, cursorIndex)}\r${value.substring(cursorIndex)}`;
                    line.update(newValue);
                    onShiftFocus(line.index + 1);
                }
                break;
            case BackspaceKeyCode:
                if (isStartOfLine) {
                    line.dequeueChars(1, true);
                    onShiftFocus(line.index - 1);
                }
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (inputRef && focused != null) {
            const index = focused === FocusPosition.Start ? 0 : value.length;
            setCursorIndex(index);
            inputRef.focus();
        }
    }, [inputRef, focused, value.length]);

    useEffect(() => {
        setValue(text);
    }, [text]);

    return (
        <input
            value={value}
            disabled={!editable}
            ref={setInputRef}
            className={classes.input}
            style={getStyles(line ? line.alignment : LineAlignment.Center, editable)}
            onFocus={(e) => {
                e.target.selectionStart = cursorIndex;
                e.target.selectionEnd = cursorIndex;
                onFocus();
            }}
            onBlur={() => {
                setCursorIndex(-1);
                onBlur();
            }}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            type="text"
        />
    );
};

export default MultiMsgLineDisplay;
